import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Modal, LoadingSkeleton } from './ui';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';

function ExamResults({ token }) {
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [terms, setTerms] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ score: '', remarks: '' });
  const [deleting, setDeleting] = useState(null);
  const [formData, setFormData] = useState({
    student_id: '', subject_id: '', term_id: '', exam_type: 'end_term', score: '', remarks: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, subjectsRes, termsRes] = await Promise.all([
        fetch('/api/v1/students', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/subjects', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/terms', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setStudents(await studentsRes.json());
      setSubjects(await subjectsRes.json());
      setTerms(await termsRes.json());
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchResults = async (studentId) => {
    try {
      const response = await fetch(`/api/v1/students/${studentId}/zimsec-report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setResults(data.results || []);
    } catch {
      setError('Failed to fetch results');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const score = parseFloat(formData.score);
    if (isNaN(score) || score < 0 || score > 100) {
      setError('Score must be 0-100');
      return;
    }
    try {
      const response = await fetch('/api/v1/exam-results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          student_id: parseInt(formData.student_id, 10),
          subject_id: parseInt(formData.subject_id, 10),
          term_id: parseInt(formData.term_id, 10),
          exam_type: formData.exam_type, score, remarks: formData.remarks || null
        })
      });
      if (response.ok) {
        setShowForm(false);
        setFormData({ student_id: '', subject_id: '', term_id: '', exam_type: 'end_term', score: '', remarks: '' });
        if (selectedStudent) fetchResults(selectedStudent);
      } else {
        setError(await safeErrorMessage(response, 'Failed to add exam result'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }
  };

  const gradeBadge = (grade) => {
    const colors = { A: 'success', B: 'info', C: 'warning', D: 'warning', E: 'danger', F: 'default' };
    return <Badge variant={colors[grade] || 'default'}>{grade}</Badge>;
  };

  const columns = [
    { header: 'Subject', accessor: 'subject' },
    { header: 'Exam Type', accessor: 'exam_type', render: (row) => row.exam_type?.replace('_', ' ') },
    { header: 'Score', render: (row) => `${row.score}%` },
    { header: 'Grade', render: (row) => gradeBadge(row.zimsec_grade) },
    { header: 'Points', render: (row) => row.points ?? '—' },
    { header: 'Remarks', render: (row) => row.remarks || '—' },
    {
      header: '',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.375rem' }}>
          <Button variant="ghost" size="sm" onClick={() => { setEditing(row); setEditForm({ score: row.score ?? '', remarks: row.remarks || '' }); setError(''); }} aria-label={`Edit ${row.subject} ${row.exam_type}`}>
            <PencilIcon style={{ width: 14, height: 14 }} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setDeleting(row); setError(''); }} aria-label={`Delete ${row.subject} ${row.exam_type}`}>
            <TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} />
          </Button>
        </div>
      ),
    },
  ];

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/v1/exam-results/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editForm),
      });
      if (response.ok) {
        setEditing(null);
        fetchResults(selectedStudent);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to update result');
        setEditing(null);
      }
    } catch {
      setError('Network error');
      setEditing(null);
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`/api/v1/exam-results/${deleting.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        fetchResults(selectedStudent);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to delete result');
      }
    } catch {
      setError('Network error');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <LoadingSkeleton type="text" rows={8} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Exam Results (ZIMSEC)</h2>
        <Button variant="primary" size="sm" onClick={() => { setShowForm(!showForm); setError(''); }}>
          <PlusIcon style={{ width: 16, height: 16 }} />
          {showForm ? 'Cancel' : 'Add Result'}
        </Button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Add Exam Result">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Student *</label>
              <select name="student_id" value={formData.student_id} onChange={(e) => setFormData({...formData, student_id: e.target.value})} required>
                <option value="">Select Student</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.full_name} ({s.class_name})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Subject *</label>
              <select name="subject_id" value={formData.subject_id} onChange={(e) => setFormData({...formData, subject_id: e.target.value})} required>
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Term *</label>
              <select name="term_id" value={formData.term_id} onChange={(e) => setFormData({...formData, term_id: e.target.value})} required>
                <option value="">Select Term</option>
                {terms.map(t => <option key={t.id} value={t.id}>Term {t.term_number} - {t.year}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Exam Type *</label>
              <select name="exam_type" value={formData.exam_type} onChange={(e) => setFormData({...formData, exam_type: e.target.value})} required>
                <option value="mid_term">Mid Term</option>
                <option value="end_term">End Term</option>
                <option value="continuous">Continuous Assessment</option>
                <option value="zimsec_mock">ZIMSEC Mock</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Score (%) *</label>
              <input type="number" name="score" step="0.01" min="0" max="100" value={formData.score} onChange={(e) => setFormData({...formData, score: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Remarks</label>
              <input type="text" name="remarks" value={formData.remarks} onChange={(e) => setFormData({...formData, remarks: e.target.value})} placeholder="Optional remarks" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <Button type="submit" variant="primary">Add Result</Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(editing)} onClose={() => setEditing(null)} title={editing ? `Edit ${editing.subject} (${(editing.exam_type || '').replace(/_/g, ' ')})` : ''}>
        {editing && (
          <form onSubmit={saveEdit}>
            <div className="form-group">
              <label htmlFor="edit-score">Score (%)</label>
              <input id="edit-score" type="number" step="0.01" min="0" max="100" value={editForm.score} onChange={(e) => setEditForm({ ...editForm, score: e.target.value })} required />
            </div>
            <div className="form-group">
              <label htmlFor="edit-remarks">Remarks</label>
              <input id="edit-remarks" type="text" maxLength={200} value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <Button type="submit" variant="primary">Save</Button>
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} title="Delete result">
        {deleting && (
          <div>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
              Delete the {(deleting.exam_type || '').replace(/_/g, ' ')} mark of {deleting.score}% for {deleting.subject}? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button variant="danger" onClick={confirmDelete}>Delete result</Button>
              <Button variant="secondary" onClick={() => setDeleting(null)}>Keep it</Button>
            </div>
          </div>
        )}
      </Modal>

      <Card title="View Student Results">
        <div className="form-group">
          <label>Select Student</label>
          <select value={selectedStudent} onChange={(e) => {
            const id = e.target.value;
            setSelectedStudent(id);
            setResults([]);
            if (id) fetchResults(id);
          }}>
            <option value="">Choose a student...</option>
            {students.map(s => <option key={s.id} value={s.id}>{s.full_name} - {s.class_name}</option>)}
          </select>
        </div>

        {selectedStudent && (
          <div style={{ marginTop: '1rem' }}>
            <Table columns={columns} data={results} emptyMessage="No results recorded for this student yet." />
          </div>
        )}
      </Card>
    </div>
  );
}

export default ExamResults;
